import React from 'react'
import { Button, Input, Label, Pagination, Badge } from '@/shared/ui'
import { Table, Thead, Tbody, Tr, Th, Td } from '@/shared/ui/Table'
import { useToast } from '@/shared/ui/Toast'
import { listVipEntitlements, createVipEntitlement, updateVipEntitlement, deleteVipEntitlement, regenerateVipToken, type VipItem } from '@/modules/vip/services/vipAdminService'
import { listPlans } from '@/modules/plans/services/planService'
import { formatPlanLabel } from '@/modules/plans/utils/formatPlanLabel'
import type { Plan } from '@/modules/plans/types'

export default function VipAdminPage() {
  const { add: toast } = useToast()
  const [items, setItems] = React.useState<VipItem[]>([])
  const [links, setLinks] = React.useState<Array<{ id: string; link: string }>>([])
  const [loading, setLoading] = React.useState(false)
  const [form, setForm] = React.useState<{ email: string; discount: string; plan: string }>({ email: '', discount: '100', plan: '' })
  const [plans, setPlans] = React.useState<Plan[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 25

  async function load() {
    setLoading(true)
    try {
      const { items, links } = await listVipEntitlements()
      setItems(items)
      setLinks(links)
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Falha ao carregar VIPs', variant: 'error', soft: true })
    } finally { setLoading(false) }
  }
  React.useEffect(() => { load() }, [])
  React.useEffect(() => {
    ;(async()=>{
      try {
        const { items } = await listPlans({ page: 1, pageSize: 200 })
        const actives = (items || []).filter(p => p.active)
        setPlans(actives)
        const vip = actives.find(p => p.id === 'vip100' || p.slug === 'vip100' || (p.name || '').toLowerCase() === 'vip-100')
        if (vip) setForm(f => ({ ...f, plan: vip.id }))
      } catch {}
    })()
  }, [])

  function linkOf(id: string): string | null {
    return links.find(l => l.id === id)?.link || null
  }

  const planLabelById = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const p of plans) map.set(p.id, formatPlanLabel(p))
    return map
  }, [plans])

  async function onCreate(e: React.FormEvent) {
    e.preventDefault()
    try {
      const email = form.email.trim().toLowerCase()
      const pct = 100
      const planRef = form.plan.trim() || 'vip100'
      if (!/.+@+.+\..+/.test(email) && !/.+@.+\..+/.test(email)) throw new Error('E-mail inválido')
      await createVipEntitlement(email, pct, planRef)
      setForm({ email: '', discount: '100', plan: form.plan || 'vip100' })
      toast({ title: 'VIP criado', description: 'Registro adicionado com sucesso', variant: 'success', soft: true })
      await load()
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Falha ao criar VIP', variant: 'error', soft: true })
    }
  }

  async function onToggleActive(it: VipItem) {
    try { await updateVipEntitlement(it.id, { active: !it.active }); await load() } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Falha ao atualizar', variant: 'error', soft: true })
    }
  }

  async function onDelete(it: VipItem) {
    if (!confirm('Remover este VIP?')) return
    try { await deleteVipEntitlement(it.id); await load() } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Falha ao remover', variant: 'error', soft: true })
    }
  }

  async function onRegenerate(it: VipItem) {
    try { const { link } = await regenerateVipToken(it.id); await load(); if (link) { await navigator.clipboard?.writeText(link).catch(()=>{}); toast({ title: 'Link regenerado', description: 'Copiado para a área de transferência', variant: 'success', soft: true }) } } catch (e: any) {
      toast({ title: 'Erro', description: e?.message || 'Falha ao regenerar', variant: 'error', soft: true })
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1550px] px-4 py-6">
      <h1 className="mb-4 text-2xl font-semibold">Gerenciar VIPs</h1>

      <form onSubmit={onCreate} className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-4">
        <div>
          <Label className="mb-1 block">E-mail</Label>
          <Input value={form.email} onChange={(e)=> setForm({ ...form, email: e.target.value })} placeholder="vip@dominio.com" required />
        </div>
        <div>
          <Label className="mb-1 block">Desconto (%)</Label>
          <Input type="number" min={100} max={100} value={form.discount} disabled placeholder="100" required />
        </div>
        <div>
          <Label className="mb-1 block">Plano (opcional — obrigatório para 100%)</Label>
          <select
            className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={form.plan || (plans.find(p=>p.id==='vip100' || p.slug==='vip100' || (p.name||'').toLowerCase()==='vip-100')?.id || 'vip100')}
            onChange={()=>{}} disabled
          >
            <option value="">Qualquer plano</option>
            {plans.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} • {(p.amount_cents/100).toLocaleString('pt-BR',{ style:'currency', currency: p.currency || 'BRL' })} / {p.interval === 'month' ? 'mês' : (p.interval === 'year' ? 'ano' : p.interval)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Button type="submit">Adicionar VIP</Button>
        </div>
      </form>

      <div className="rounded-md">
        <Table className="min-w-full">
          <Thead>
            <Tr>
              <Th>E-mail</Th>
              <Th>Desconto</Th>
              <Th>Plano</Th>
              <Th>Token/Link</Th>
              <Th>Ativo</Th>
              <Th>Usado</Th>
              <Th className="text-right">Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading && (
              <Tr><Td colSpan={7} className="text-center">Carregando...</Td></Tr>
            )}
            {!loading && items.length === 0 && (
              <Tr><Td colSpan={7} className="text-center text-foreground/70">Nenhum VIP cadastrado.</Td></Tr>
            )}
            {items.slice((page-1)*pageSize, (page-1)*pageSize + pageSize).map((it) => (
              <Tr key={it.id}>
                <Td>{it.email}</Td>
                <Td>{Number(it.discount_percent).toFixed(0)}%</Td>
                <Td>{it.plan_id ? (planLabelById.get(it.plan_id) || it.plan_id) : 'qualquer'}</Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{it.token.slice(0, 10)}…</code>
                    <Button variant="outline" onClick={async ()=> { const link = linkOf(it.id); if (link) { await navigator.clipboard?.writeText(link).catch(()=>{}); toast({ title: 'Link copiado', description: link, soft: true }) } }}>Copiar link</Button>
                  </div>
                </Td>
                <Td>
                  {it.active
                    ? <Badge variant="soft" color="success">Ativo</Badge>
                    : <Badge variant="soft" color="error">Inativo</Badge>}
                </Td>
                <Td>{it.used_at ? new Date(it.used_at).toLocaleDateString() : '-'}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={()=> onToggleActive(it)}>{it.active ? 'Desativar' : 'Ativar'}</Button>
                    <Button variant="outline" onClick={()=> onRegenerate(it)}>Regenerar</Button>
                    <Button variant="destructive" onClick={()=> onDelete(it)}>Remover</Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
      <Pagination
        className="mt-4"
        page={page}
        total={Math.max(1, Math.ceil(items.length / pageSize))}
        onChange={setPage}
      />
    </div>
  )
}
